import { createClient } from '@sanity/client';
import { createImageUrlBuilder } from '@sanity/image-url';

export const sanityClient = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID,
  dataset: import.meta.env.VITE_SANITY_DATASET,
  apiVersion: import.meta.env.VITE_SANITY_API_VERSION,
  useCdn: true,
});

const builder = createImageUrlBuilder(sanityClient);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function urlFor(source: any) {
  return builder.image(source);
}

// Types
export interface SanityAuthor {
  _id: string;
  name: string;
  slug: { current: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  image?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bio?: any[];
}

export interface SanityCategory {
  _id: string;
  title: string;
  slug: { current: string };
  description?: string;
}

export interface SanityTag {
  _id: string;
  title: string;
  slug: { current: string };
}

export interface SanityCallToAction {
  _type: 'callToAction';
  _key: string;
  heading: string;
  text?: string;
  buttonText: string;
  buttonUrl: string;
  style: 'primary' | 'secondary' | 'outline';
}

export interface SanityPost {
  _id: string;
  title: string;
  slug: { current: string };
  author?: SanityAuthor;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mainImage?: any;
  categories?: SanityCategory[];
  tags?: SanityTag[];
  publishedAt: string;
  excerpt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any[];
  seoTitle?: string;
  seoDescription?: string;
  estimatedReadingTime?: number;
}

// GROQ Queries
export const allPostsQuery = `
  *[_type == "post" && publishedAt <= now()] | order(publishedAt desc) {
    _id,
    title,
    slug,
    "author": author->{
      _id,
      name,
      slug,
      image
    },
    mainImage,
    "categories": categories[]->{
      _id,
      title,
      slug
    },
    "tags": tags[]->{
      _id,
      title,
      slug
    },
    publishedAt,
    excerpt,
    "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
  }
`;

export const postBySlugQuery = `
  *[_type == "post" && slug.current == $slug][0] {
    _id,
    title,
    slug,
    "author": author->{
      _id,
      name,
      slug,
      image,
      bio
    },
    mainImage,
    "categories": categories[]->{
      _id,
      title,
      slug
    },
    "tags": tags[]->{
      _id,
      title,
      slug
    },
    publishedAt,
    excerpt,
    body,
    seoTitle,
    seoDescription,
    "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
  }
`;

export const postsByCategoryQuery = `
  *[_type == "post" && publishedAt <= now() && $categorySlug in categories[]->slug.current] | order(publishedAt desc) {
    _id,
    title,
    slug,
    "author": author->{
      _id,
      name,
      slug,
      image
    },
    mainImage,
    "categories": categories[]->{
      _id,
      title,
      slug
    },
    "tags": tags[]->{
      _id,
      title,
      slug
    },
    publishedAt,
    excerpt,
    "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
  }
`;

export const allCategoriesQuery = `
  *[_type == "category"] | order(title asc) {
    _id,
    title,
    slug,
    description
  }
`;

export const allTagsQuery = `
  *[_type == "tag"] | order(title asc) {
    _id,
    title,
    slug
  }
`;

export const relatedPostsQuery = `
  *[_type == "post" && publishedAt <= now() && slug.current != $currentSlug && count(categories[@._ref in $categoryIds]) > 0] | order(publishedAt desc) [0...3] {
    _id,
    title,
    slug,
    mainImage,
    "categories": categories[]->{
      _id,
      title,
      slug
    },
    publishedAt,
    excerpt,
    "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
  }
`;
